import DOMPurify from "dompurify";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { useConfiguration } from "../contexts/ConfigurationProvider";
import { useHeader } from "../contexts/HeaderContext";

// The PMS editor only offers these inline formats, and the backend sanitizes
// to the same allowlist on save — this is the rendering-side belt to that
// suspenders.
const ALLOWED_TAGS = ["p", "br", "strong", "b", "em", "i", "u", "s"];

// gap-16 (4 rem) between the two marquee copies, and ~50 px/s scroll speed.
const MARQUEE_GAP_PX = 64;
const MARQUEE_PX_PER_SECOND = 50;

const codePointToText = (cp: number) =>
  cp > 0 && cp <= 0x10ffff ? String.fromCodePoint(cp) : "";

// `\s` already covers every Unicode space (incl. U+00A0, U+2000–U+200A,
// U+202F, U+3000, U+FEFF); the rest are zero-width/soft-hyphen code points
// that render as nothing.
const INVISIBLE = /[\s\u00ad\u200b-\u200f\u2060]/g;
// Every HTML5 named entity whose character renders as whitespace or zero-width.
const NAMED_INVISIBLE_ENTITIES =
  /&(nbsp|NonBreakingSpace|NoBreak|ensp|emsp|emsp13|emsp14|numsp|puncsp|thinsp|ThinSpace|hairsp|VeryThinSpace|MediumSpace|ZeroWidthSpace|NegativeVeryThinSpace|NegativeThinSpace|NegativeMediumSpace|NegativeThickSpace|zwnj|zwj|lrm|rlm|wj|shy|Tab|NewLine|InvisibleTimes|InvisibleComma|ApplyFunction|it|ic|af);/gi;

// Mirrors hasVisibleText in backend/src/shared/utils/html.ts.
const hasVisibleText = (html: string) =>
  html
    .replace(/<[^>]*>/g, "")
    .replace(/&#x([0-9a-f]+);/gi, (_, hex: string) =>
      codePointToText(parseInt(hex, 16)),
    )
    .replace(/&#(\d+);/g, (_, dec: string) => codePointToText(Number(dec)))
    .replace(NAMED_INVISIBLE_ENTITIES, "")
    .replace(INVISIBLE, "").length > 0;

/**
 * The site message bar pinned above the header. `configuration.message` is
 * already narrowed by the backend to the message whose range contains the
 * site's local today, and the PMS forces a text on every active language —
 * so a missing text for the selected language means no bar.
 */
export const MessageBar = () => {
  const { configuration } = useConfiguration();
  const { selectedLanguage } = useHeader();

  // The backend already omits `message` without the flag; this keeps the bar
  // dark for a config cached from before the flag was switched off.
  const messagesEnabled =
    configuration?.featureFlags?.includes("wbe-messages") ?? false;
  const html = messagesEnabled
    ? configuration?.message?.texts[selectedLanguage]
    : undefined;
  const sanitized = html
    ? DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR: [] })
    : "";
  const hasText = hasVisibleText(sanitized);

  const containerRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLSpanElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [copyWidth, setCopyWidth] = useState(0);
  // Read once: under reduced motion the overflowing copy wraps, and letting
  // that re-measure would flip it between wrap and nowrap forever.
  const [reducedMotion] = useState(
    () =>
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false,
  );

  const marquee = overflowing && !reducedMotion;

  // `marquee` in the deps: flipping it swaps the layout, so copyRef points at
  // a new node and the observer must be re-created against the current copy.
  // scrollWidth vs clientWidth measures the same in both layouts, so the
  // re-measure is stable rather than oscillating.
  useEffect(() => {
    if (reducedMotion || !hasText || typeof ResizeObserver === "undefined")
      return;
    // scrollWidth = the content's full nowrap width; clientWidth = what the
    // clip container actually shows.
    const measure = () => {
      const copy = copyRef.current?.scrollWidth ?? 0;
      const clip = containerRef.current?.clientWidth ?? 0;
      setCopyWidth(copy);
      setOverflowing(copy > clip);
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (containerRef.current) observer.observe(containerRef.current);
    if (copyRef.current) observer.observe(copyRef.current);
    return () => observer.disconnect();
  }, [sanitized, hasText, reducedMotion, marquee]);

  if (!hasText) return null;

  return (
    <div
      data-testid="wbe-message-bar"
      role="status"
      className="relative z-10 bg-primary px-4 py-2 text-xs text-on-primary sm:text-sm"
    >
      <div ref={containerRef} className="overflow-hidden">
        {marquee ? (
          <div
            data-testid="wbe-message-bar-track"
            // --marquee-distance = one copy plus the gap, so the second copy
            // lands exactly where the first started when the loop wraps.
            className="flex w-max animate-marquee gap-16 whitespace-nowrap hover:[animation-play-state:paused]"
            style={
              {
                "--marquee-distance": `calc(-1 * (${copyWidth}px + 4rem))`,
                "--marquee-duration": `${(copyWidth + MARQUEE_GAP_PX) / MARQUEE_PX_PER_SECOND}s`,
              } as CSSProperties
            }
          >
            {/* <p>/<br> are forced inline so the sanitized markup flows as one
                line in the track. */}
            <span
              ref={copyRef}
              data-testid="wbe-message-bar-copy"
              className="[&_br]:hidden [&_p]:inline"
              dangerouslySetInnerHTML={{ __html: sanitized }}
            />
            <span
              aria-hidden
              data-testid="wbe-message-bar-copy"
              className="[&_br]:hidden [&_p]:inline"
              dangerouslySetInnerHTML={{ __html: sanitized }}
            />
          </div>
        ) : (
          // Reduced motion or a text that fits: one centered copy, wrapping
          // under reduced motion so an overflowing text never animates.
          <span
            ref={copyRef}
            data-testid="wbe-message-bar-copy"
            className={`block text-center [&_p]:inline ${reducedMotion ? "whitespace-normal" : "whitespace-nowrap"}`}
            dangerouslySetInnerHTML={{ __html: sanitized }}
          />
        )}
      </div>
    </div>
  );
};
